<script setup>
import {ref, watch, computed, onMounted, onUnmounted} from 'vue'
import {useStore} from 'vuex'
import { toTypedSchema } from '@vee-validate/zod'
import * as z from 'zod'
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine'
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { pointerOutsideOfPreview } from '@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview'
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview'

import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/toast'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'

import {RadioGroup, RadioGroupItem} from "@/components/ui/radio-group";
import {Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select"
import {NotepadTextDashed, CircleMinus, GripVertical} from 'lucide-vue-next'
import ColorPicker from '@/components/ui/ColorPicker.vue'

const store = useStore()

const props = defineProps({
  editMode: Boolean,
  template: Object,
  projectId: Number
})

const emit = defineEmits(['template-updated', 'close'])

const isOpen = ref(false)
const isProcessing = ref(false)

const selectedColor = ref(props.editMode ? props.template?.color : '#E53E3E')

const close = () => {
  isOpen.value = false
}

const MAX_FILE_SIZE = 100 * 100 * 5
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"]
const ACCEPTED_IMAGE_RESOLUTION = { width: 200, height: 200 }

const units = ['mi', 'in', 'ft', 'lb', 'm', 'km', 'kg', 'm/s']
const geometryChoices = ['Point', 'LineString', 'Polygon']
const defaultTypes = [
  { name: 'Text', value: 0 },
  { name: 'Number', value: 1 },
  { name: 'Yes/No', value: 2 },
  { name: 'Multi-line text', value: 3 },
  { name: 'Choices', value: 4 },
]


const schema = z.object({
  name: z.string().default(props.editMode ? props.template.name : ''),
  color: z.string().default(props.editMode ? props.template.color : selectedColor.value),
  geometry: z.string().default(props.editMode ? props.template.geometry_type : geometryChoices[0]),
  shared: z.boolean().default(props.editMode ? props.template.shared : false),
  geofence: z.boolean().default(props.editMode ? props.template.geofence : false),
  geofenceEvents: z.string().default(props.editMode ? props.template.geofence_events : null).optional(),
  imageRequired: z.boolean().default(props.editMode ? props.template.image_required : false),
  icon: z.any()
    .optional()
    .refine(
      (file) => file === undefined || ACCEPTED_IMAGE_TYPES.includes(file?.type),
      ".jpg, .jpeg, .png and .webp files are accepted."
    )
    .refine((file) => file === undefined || file?.size <= MAX_FILE_SIZE, `Max file size is ${MAX_FILE_SIZE / 1000}KB`)
})


const formSchema = toTypedSchema(schema)

const isDataFields = ref(props.editMode ? !!props.template.data_fields : false)
const dataFields = ref(props.editMode ? JSON.parse(JSON.stringify(props.template.data_fields)) : [])
const draggedFieldIndex = ref(null)
const dragOverFieldIndex = ref(null)
const fieldRefs = ref([])

// Check if we're in admin context (projectId is undefined for admin)
const isAdminContext = computed(() => props.projectId === undefined)
const isSystemTemplate = ref(props.editMode && props.template ? props.template.is_system_template : false)

const addDataField = () => {
  dataFields.value.push({ key: '', type: 0, options: []})
  // Re-initialize drag and drop after adding new field
  setTimeout(() => {
    reinitializeDragDrop()
  }, 100)
}

const reinitializeDragDrop = () => {
  // Clean up previous drag and drop
  cleanupFunctions.value.forEach(cleanup => cleanup && cleanup())
  cleanupFunctions.value = []
  
  // Set up drag and drop for each field
  fieldRefs.value.forEach((ref, index) => {
    if (ref) {
      const cleanup = setupFieldDragDrop(ref, index)
      if (cleanup) cleanupFunctions.value.push(cleanup)
    }
  })
}
const removeDataField = (index) => {
  dataFields.value.splice(index, 1)
  updateFieldIndices()
}

const updateFieldIndices = () => {
  dataFields.value.forEach((field, index) => {
    field.index = index
  })
}

const addOption = (field) => {
  field.options.push({ value: '', label: '' });
}
const removeOption = (field, index) => {
  field.options.splice(index, 1);
}

const onSubmit = async (values) => {
  isProcessing.value = true

  // Ensure all fields have indices before submission
  updateFieldIndices()

  const payload = {
    name: values.name,
    color: selectedColor.value,
    geometry_type: values.geometry,
    shared: values.shared,
    geofence: values.geofence,
    geofence_events: values.geofenceEvents,
    data_fields: dataFields.value.length > 0 ? dataFields.value.map((field, idx) => ({
      ...field,
      index: field.index !== undefined ? field.index : idx
    })) : null,
    image_required: values.imageRequired
  }

  // Automatic project connection:
  // - If projectId is provided (regular user in project), connect to that project
  // - If projectId is undefined (admin creating template), don't connect to any project
  if (props.projectId !== undefined) {
    payload.project_id = props.projectId
  } else {
    // Admin context: create template without project connection
    payload.project_ids = []
    // Set is_system_template in admin context
    payload.is_system_template = isSystemTemplate.value
  }

  if (props.editMode) {
    await store.dispatch('geo/updateReportTemplate', {
      project_id: props.projectId,
      template: { ...props.template, ...payload },
      icon: values.icon
    })
  } else {
    await store.dispatch('geo/createReportTemplate', {
      template: payload,
      icon: values.icon
    })
  }

  // Only fetch project templates if projectId is provided (regular project context)
  if (props.projectId !== undefined) {
    await store.dispatch('geo/fetchProjectReportTemplates', props.projectId)
  }

  toast({
    title: `Template ${values.name} ${props.editMode ? 'updated' : 'created'}`,
  })

  // Emit event to notify parent component (for admin context)
  emit('template-updated')

  isProcessing.value = false
  close()
}

const selectedIcon = ref(null)
const selectedIconLink = computed(() => {
  if (!selectedIcon.value) return null
  return URL.createObjectURL(selectedIcon.value)
})
const setSelectedIcon = (event) => {
  selectedIcon.value = event.target.files[0]
}


// Drag and Drop functionality
const setupFieldDragDrop = (element, index) => {
  if (!element) return
  
  const cleanup = combine(
    draggable({
      element,
      dragHandle: element.querySelector('.drag-handle'),
      getInitialData: () => ({ index }),
      onDragStart: () => {
        draggedFieldIndex.value = index
      },
      onDrop: () => {
        draggedFieldIndex.value = null
        dragOverFieldIndex.value = null
      },
      onGenerateDragPreview: ({ nativeSetDragImage }) => {
        setCustomNativeDragPreview({
          getOffset: pointerOutsideOfPreview({ x: '16px', y: '8px' }),
          render: ({ container }) => {
            const preview = document.createElement('div')
            preview.className = 'bg-white shadow-lg rounded-md px-3 py-1.5 text-sm'
            preview.textContent = dataFields.value[index].key || 'Field ' + (index + 1)
            container.appendChild(preview)
          },
          nativeSetDragImage,
        })
      },
    }),
    
    dropTargetForElements({
      element,
      canDrop: ({ source }) => {
        return source.data.index !== index
      },
      getData: () => ({ index }),
      onDragEnter: () => {
        dragOverFieldIndex.value = index
      },
      onDragLeave: () => {
        dragOverFieldIndex.value = null
      },
      onDrop: ({ source }) => {
        const fromIndex = source.data.index
        const toIndex = index
        
        if (fromIndex !== toIndex) {
          // Reorder the fields
          const [movedField] = dataFields.value.splice(fromIndex, 1)
          dataFields.value.splice(toIndex, 0, movedField)
          updateFieldIndices()
        }
        
        draggedFieldIndex.value = null
        dragOverFieldIndex.value = null
      },
    })
  )
  
  return cleanup
}

// Setup drag and drop when fields change
watch(dataFields, () => {
  updateFieldIndices()
}, { deep: true })

onMounted(() => {
  if (props.editMode && props.template?.data_fields) {
    // Ensure indices are set for existing fields
    dataFields.value = props.template.data_fields.map((field, index) => ({
      ...field,
      index: field.index !== undefined ? field.index : index
    }))
    // Sort by index
    dataFields.value.sort((a, b) => a.index - b.index)

    // Initialize drag and drop if data fields are visible
    if (isDataFields.value) {
      setTimeout(() => {
        reinitializeDragDrop()
      }, 100)
    }
  }
})

const cleanupFunctions = ref([])

watch(isDataFields, (newVal) => {
  if (newVal) {
    // Set up drag and drop for each field after DOM updates
    setTimeout(() => {
      reinitializeDragDrop()
    }, 100)
  }
})

watch(isOpen, (newVal) => {
  if (!newVal) {
    // Dialog was closed, emit close event
    emit('close')
  }
})

onUnmounted(() => {
  cleanupFunctions.value.forEach(cleanup => cleanup && cleanup())
})
</script>

<template>
  <Dialog :open="isOpen" @update:open="isOpen = $event">
    <DialogTrigger class="w-full" as-child>
      <slot>
        <Button variant="ghost" class="w-full h-8 px-2 flex justify-start">{{ props.editMode ? 'Edit' : 'Create' }}</Button>
      </slot>
    </DialogTrigger>
    <DialogContent class="max-w-[350px] max-h-[80vh] overflow-y-auto">
      <div data-testid="create-template-dialog">
        <DialogHeader>
          <DialogTitle v-if="editMode">Edit template</DialogTitle>
          <DialogTitle v-else>Create template</DialogTitle>
          <DialogDescription></DialogDescription>
        </DialogHeader>

      <Form class="space-y-3" :validation-schema="formSchema" @submit="onSubmit" v-slot="{ getValues }" data-testid="template-form">
        <div class="flex space-x-3">
          <div class="basis-5/6">
            <FormField v-slot="{ componentField }" name="name">
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input v-bind="componentField" data-testid="template-name-input" />
                </FormControl>
                <FormMessage />
              </FormItem>
            </FormField>
          </div>
          <div class="basis-1/6">
            <FormField v-slot="{ componentField }" name="color">
              <FormItem>
                <FormLabel>Color</FormLabel>
                <FormControl>
                  <div class="pt-1">
                    <input type="hidden" v-bind="componentField" :value="selectedColor" />
                    <ColorPicker v-model="selectedColor" />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            </FormField>
          </div>
        </div>

        <FormField v-slot="{ componentField }" name="geometry">
          <FormItem>
            <FormLabel>Geometry</FormLabel>
            <FormControl>
              <Select v-bind="componentField" data-testid="template-geometry-select">
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem v-for="choice in geometryChoices" :key="choice" :value="choice">{{ choice }}</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <FormField v-slot="{ value, handleChange }" name="shared">
          <FormItem>
            <FormControl>
              <div class="flex items-center">
                <Switch
                  :model-value="value"
                  @update:model-value="handleChange"
                  data-testid="template-shared-switch"
                />
                <Label class="ml-2">Public</Label>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <FormField v-slot="{ value, handleChange }" name="imageRequired">
          <FormItem>
            <FormControl>
              <div class="flex items-center">
                <Switch
                  :model-value="value"
                  @update:model-value="handleChange"
                  data-testid="template-image-required-switch"
                />
                <Label class="ml-2">Image required</Label>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <FormField v-if="getValues().geometry === 'Polygon'" v-slot="{ value, handleChange }" name="geofence">
          <FormItem>
            <FormControl>
              <div class="flex items-center">
                <Switch
                  :model-value="value"
                  @update:model-value="handleChange"
                  data-testid="template-geofence-switch"
                />
                <Label class="ml-2">Geofence</Label>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <div>
          <FormField
            v-if="getValues().geofence"
            v-slot="{ componentField }"
            name="geofenceEvents"
          >
            <FormItem>
              <FormControl>
                <RadioGroup v-bind="componentField" class="flex space-x-3" data-testid="template-geofence-events">
                  <FormItem class="flex items-center space-y-0 gap-x-3">
                    <FormControl>
                      <RadioGroupItem value="in" data-testid="geofence-event-in" />
                    </FormControl>
                    <FormLabel class="font-normal">In</FormLabel>
                  </FormItem>
                  <FormItem class="flex items-center space-y-0 gap-x-3">
                    <FormControl>
                      <RadioGroupItem value="out" data-testid="geofence-event-out" />
                    </FormControl>
                    <FormLabel class="font-normal">Out</FormLabel>
                  </FormItem>
                  <FormItem class="flex items-center space-y-0 gap-x-3">
                    <FormControl>
                      <RadioGroupItem value="both" data-testid="geofence-event-both" />
                    </FormControl>
                    <FormLabel class="font-normal">Both</FormLabel>
                  </FormItem>
                </RadioGroup>
              </FormControl>
            </FormItem>
            <FormMessage />
          </FormField>
        </div>

        <div class="flex items-center">
          <Switch :model-value="isDataFields" @update:model-value="() => isDataFields = !isDataFields" data-testid="template-add-data-fields-switch" />
          <Label class="ml-2">Add data fields</Label>
        </div>

        <FormField v-if="isDataFields" v-slot="{ value }" name="data_fields">
          <FormItem>
            <FormLabel>Data Fields</FormLabel>
            <FormControl>
              <div 
                v-for="(field, index) in dataFields" 
                :key="field.id || `field-${index}`" 
                :ref="el => fieldRefs[index] = el"
                class="flex flex-col space-y-2 mb-2 p-2 rounded border transition-all"
                :class="{
                  'opacity-50': draggedFieldIndex === index,
                  'border-blue-400 bg-blue-50': dragOverFieldIndex === index,
                  'border-gray-200': dragOverFieldIndex !== index
                }"
              >
                <div class="flex flex-row items-center space-x-2">
                  <div class="drag-handle cursor-move flex items-center px-1 text-gray-400 hover:text-gray-600">
                    <GripVertical class="w-4 h-4" />
                  </div>
                  <div class="flex-1">
                    <Input v-model="field.key" placeholder="Label" :data-testid="`template-field-name-${index}`" />
                  </div>
                  <div class="w-32">
                    <Select v-model="field.type" :data-testid="`template-field-type-${index}`">
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem v-for="type in defaultTypes" :key="type" :value="type.value">{{ type.name }}</SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Button type="button" variant="ghost" @click="removeDataField(index)" v-if="dataFields.length > 0" class="px-1 text-red-400 hover:text-red-500" :data-testid="`template-remove-field-${index}`">
                      <CircleMinus class="w-5 h-5" />
                    </Button>
                  </div>
                </div>

                <div v-if="field.type === 4" class="border border-gray-300 rounded p-2 space-y-1">
                  <FormLabel>Options</FormLabel>
                  <div v-for="(option, optionIndex) in field.options" :key="optionIndex" class="flex flex-row space-x-2">
                    <div class="w-full">
                      <Input v-model="field.options[optionIndex]" placeholder="Option" :data-testid="`template-field-option-${index}-${optionIndex}`" />
                    </div>
                    <div>
                      <Button type="button" variant="ghost" @click="removeOption(field, optionIndex)" class="px-1 text-red-400 hover:text-red-500" :data-testid="`template-remove-option-${index}-${optionIndex}`">
                        <CircleMinus class="w-5 h-5" />
                      </Button>
                    </div>
                  </div>
                  <Button type="button" @click="field.options.push('')" variant="secondary" size="sm" :data-testid="`template-add-option-${index}`">+ Add Option</Button>
                </div>
              </div>

              <Button type="button" @click="addDataField" variant="secondary" size="sm" :disabled="dataFields.length > 30" data-testid="template-add-field-button">+ Add Field</Button>
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <FormField v-slot="{ componentField }" name="icon">
          <FormItem>
            <FormLabel>Icon</FormLabel>
            <FormControl>
              <input
                type="file"
                accept=".jpg, .jpeg, .png, .webp"
                @change="setSelectedIcon"
                v-bind="componentField"
                class="text-sm"
                data-testid="template-icon-input"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <div v-if="selectedIcon">
          <img :src="selectedIconLink" alt="icon" width="30" height="30" />
        </div>
        <div v-else-if="props.editMode && props.template">
          <img v-if="props.template.s3_icon" :src="props.template.s3_icon" alt="icon" width="30" height="30" />
          <NotepadTextDashed v-else />
        </div>

        <!-- Admin-Only Settings Section -->
        <div v-if="isAdminContext" class="!mt-6">
          <div class="px-3 py-3 bg-blue-50 border border-blue-200 rounded-md">
            <div class="flex items-center gap-2 mb-3">
              <span class="text-xs font-semibold text-blue-800 uppercase tracking-wide">Admin Only</span>
            </div>

            <div class="flex items-center">
              <Switch
                v-model="isSystemTemplate"
                data-testid="template-system-template-switch"
              />
              <Label class="ml-2 font-medium">System Template</Label>
            </div>
          </div>
        </div>

        <DialogFooter>
          <div class="mt-4">
            <Button variant="ghost" type="button" @click="close" data-testid="template-close-button">Close</Button>
            <Button type="submit" v-if="editMode" :disabled="isProcessing" data-testid="template-save-button">
              <Spinner v-if="isProcessing" class="w-4 h-4 mr-2" />
              <span v-else>Save changes</span>
            </Button>
            <Button type="submit" v-else :disabled="isProcessing" data-testid="template-create-button">
              <Spinner v-if="isProcessing" class="w-4 h-4 mr-2" />
              <span v-else>Create</span>
            </Button>
          </div>
        </DialogFooter>
      </Form>
      </div>
    </DialogContent>
  </Dialog>
</template>
